import { cn } from '../lib/utils';

/**
 * Verlengde dienst: een dienst met een verlenging (bv. rooster 07:00–15:00, doorgewerkt tot 15:30)
 * is in de app één dienst; de losse blokken staan in shift.blokken. Zelfde logica als
 * worker/src/lib/verlenging.ts.
 */

interface Blok {
  start: string;
  end: string;
}

type MetBlokken = { start_time: string; end_time: string; blokken?: string | null };

const min = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const duur = (b: Blok) => (min(b.end) - min(b.start) + 1440) % 1440 || 1440;

export interface Verlenging {
  rooster: Blok;
  voor: Blok | null;
  na: Blok | null;
  minuten: number;
}

export function verlengingVan(s: MetBlokken): Verlenging | null {
  if (!s.blokken) return null;
  let blokken: Blok[];
  try {
    blokken = JSON.parse(s.blokken);
  } catch {
    return null;
  }
  if (!Array.isArray(blokken) || blokken.length < 2) return null;
  const rooster = blokken.reduce((a, b) => (duur(b) > duur(a) ? b : a));
  const voor = s.start_time.slice(0, 5) !== rooster.start ? { start: s.start_time.slice(0, 5), end: rooster.start } : null;
  const na = s.end_time.slice(0, 5) !== rooster.end ? { start: rooster.end, end: s.end_time.slice(0, 5) } : null;
  return { rooster, voor, na, minuten: (voor ? duur(voor) : 0) + (na ? duur(na) : 0) };
}

export const fmtDuur = (m: number) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

export function verlengingTekst(v: Verlenging): string {
  return [
    `Rooster ${v.rooster.start}–${v.rooster.end}`,
    v.voor ? `eerder begonnen ${v.voor.start}–${v.voor.end}` : null,
    v.na ? `langer doorgewerkt ${v.na.start}–${v.na.end}` : null,
  ]
    .filter(Boolean)
    .join(', ');
}

/** Klein label achter de tijden, bv. "+0:30". Niets bij een gewone dienst. */
export function VerlengdTag({ shift, className }: { shift: MetBlokken; className?: string }) {
  const v = verlengingVan(shift);
  if (!v) return null;
  return (
    <span className={cn('t-num whitespace-nowrap text-xs font-medium text-amber-200', className)} title={`Verlengd: ${verlengingTekst(v)}`}>
      +{fmtDuur(v.minuten)}
      <span className="sr-only"> verlengd, {verlengingTekst(v)}</span>
    </span>
  );
}
